/**
 * Consult Scheduling — PURE data model (Stage 1 · credential-free foundation).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS
 * ─────────────────────────────────────────────────────────────────────────
 *  The TypeScript interfaces for the "One Availability, Two Front Doors"
 *  Consult Scheduling module, implemented exactly as Silas specced them in
 *  `Projects/01 AVBP/specs/CONSULT-SCHEDULING-DATA-MODEL.md`. This file is the
 *  PURE layer — zero Firebase, zero React — so the Node native test runner
 *  exercises it directly via `test/_tsload.cjs`, mirroring the discipline of
 *  `src/lib/leads.ts` (pure model) + `src/lib/leadStore.ts` (Firestore seam).
 *
 *  House-style decisions this matches (citations in the data-model doc §0):
 *    · ISO strings live in the pure model; `serverTimestamp()` only ever in
 *      the impure store wrappers (cf. leadStore.leadToDoc).
 *    · Foreign-key fields store the target's id/slug, never the title
 *      (hostStaffId → staff/{id}, leadId → leads/{id}, contactId → clients/{cid}).
 *    · Snapshot-on-write protects history — the resolved type values are
 *      frozen onto each booking (`ConsultationTypeSnapshot`) exactly like the
 *      convert bridge freezes gate money in leads.ts.
 *    · Weekday / minutes-past-midnight / IANA-zone vocabulary is shared with
 *      `src/lib/workingCalendar.ts` so both calendars speak one language.
 *
 *  NONE of the impure pieces (consultationStore, appointmentTypesStore,
 *  availabilityStore, bookingLinkStore, the Cloud Functions, Door A/B UI) are
 *  in this file — this is the credential-free foundation only.
 * ─────────────────────────────────────────────────────────────────────────
 */

/* ════════════════════ Shared vocabulary ════════════════════ */

/**
 * Reuse the `workingCalendar.ts` weekday convention: 0 = Sunday … 6 = Saturday
 * (JS `getDay`). One language across both calendars.
 */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** Who a type / window / slot is aimed at (plan §6 audience tags). */
export type Audience = 'indian' | 'international' | 'all';

/**
 * Price is FREE now, paid later (plan §6 answer 4 — FREE discovery now).
 * `amount` / `currency` are null when `mode === 'free'`.
 */
export interface TypePrice {
  mode: 'free' | 'paid';
  amount: number | null;
  currency: 'INR' | 'USD' | null;
}

/* ════════════════════ Availability — settings/availability ════════════════════ */

/** One bookable window on a given weekday, in the stored zone (IST). */
export interface AvailabilityWindow {
  /** Minutes past local midnight, e.g. 480 = 08:00, 660 = 11:00. */
  startMin: number;
  /** Minutes past local midnight the window closes, e.g. 540 = 09:00. */
  endMin: number;
  /** Audience tag for the window (plan §6 table). */
  audience: Audience;
  /** Optional human label, e.g. "post-office". */
  label?: string;
}

/**
 * Per-weekday window arrays — different / multiple windows per day, freely
 * editable by staff (plan §6 "Editable & per-weekday"). An empty array means
 * the day is off (Sundays). This is the canonical name in the build brief;
 * `WeeklyWindows` is exported below as an alias for the data-model doc's name.
 */
export type WeeklyAvailability = Record<Weekday, AvailabilityWindow[]>;

/** Data-model doc alias — `WeeklyWindows` and `WeeklyAvailability` are one type. */
export type WeeklyWindows = WeeklyAvailability;

/**
 * Per-type availability: reuse the global windows, or carry the type's own.
 * The custom shape is the SAME `WeeklyAvailability` used by settings/availability
 * so one renderer / resolver serves both (data-model §2).
 */
export type TypeAvailability =
  | { mode: 'global' }
  | { mode: 'custom'; weekly: WeeklyAvailability };

/** The single `settings/availability` doc — global windows + booking rules. */
export interface AvailabilitySettings {
  /** Stored truth zone — Asia/Kolkata (plan §6 timezone note). */
  timeZone: string;
  /** Per-weekday windows. */
  weekly: WeeklyAvailability;

  /* — global booking rules (plan §6 "Rules") — */
  defaultSlotMin: number; //       30
  defaultBufferMin: number; //     15, never back-to-back
  minNoticeHours: number; //       2-hour minimum advance notice
  maxPerDay: number; //            max 6 bookings/day
  bookableHorizonDays: number; //  bookable up to 60 days out

  /**
   * Hard blocks (plan §6): full non-working calendar days. Same shape as
   * `workingCalendar.ts OrgHoliday` so the festival list can be shared with
   * `settings/orgHolidays`. Sundays are expressed as `weekly[0] = []`.
   */
  hardBlocks: Array<{ date: string; label: string }>;

  /* — audit (ISO strings; serverTimestamp filled in the wrapper) — */
  updatedAt: string;
  updatedBy: string;
}

/**
 * The five locked default windows, applied Mon–Sat (plan §6 table); Sunday off.
 * This is the SEED for `settings/availability.weekly` — staff edit freely after.
 *
 *   08:00–09:00  international  · US West eve / Europe early / ME morning
 *   11:00–12:00  indian (pref) · homemakers, retirees, self-employed
 *   15:00–17:00  international  · Europe midday / UK late morning
 *   18:30–19:30  indian (post-office) · working professionals after office
 *   20:00–21:30  international  · US East morning / UK afternoon
 */
export const DEFAULT_WEEKLY: WeeklyAvailability = (() => {
  const day: AvailabilityWindow[] = [
    { startMin: 8 * 60, endMin: 9 * 60, audience: 'international' },
    { startMin: 11 * 60, endMin: 12 * 60, audience: 'indian', label: 'preferred' },
    { startMin: 15 * 60, endMin: 17 * 60, audience: 'international' },
    { startMin: 18 * 60 + 30, endMin: 19 * 60 + 30, audience: 'indian', label: 'post-office' },
    { startMin: 20 * 60, endMin: 21 * 60 + 30, audience: 'international' },
  ];
  return { 0: [], 1: day, 2: day, 3: day, 4: day, 5: day, 6: day };
})();

/**
 * The locked default rules + the five windows as one `settings/availability`
 * seed. Encodes: 30-min duration, 15-min buffer (never back-to-back), 2-hour
 * minimum notice, max 6 bookings/day, 60-day horizon, Sundays off, and the
 * major-festival hard blocks (reused from the working-calendar org holidays).
 * Staff edit freely after seeding.
 */
export const DEFAULT_AVAILABILITY: AvailabilitySettings = {
  timeZone: 'Asia/Kolkata',
  weekly: DEFAULT_WEEKLY,
  defaultSlotMin: 30,
  defaultBufferMin: 15,
  minNoticeHours: 2,
  maxPerDay: 6,
  bookableHorizonDays: 60,
  hardBlocks: [
    { date: '2026-03-04', label: 'Holi' },
    { date: '2026-03-05', label: 'Holi' },
    { date: '2026-10-20', label: 'Dussehra' },
    { date: '2026-11-08', label: 'Deepawali' },
    { date: '2026-11-09', label: 'Deepawali' },
  ],
  updatedAt: '1970-01-01T00:00:00.000Z',
  updatedBy: 'system (seed)',
};

/* ════════════════════ appointmentTypes/{id} ════════════════════ */

export interface AppointmentType {
  /** Stable id (AT-#### serial, e.g. AT-0001 — same discipline as leadId). */
  id: string;
  /** Display name, e.g. "Discovery call". */
  name: string;
  /** kebab-case ASCII slug used in /book/:slug — unique, e.g. "discovery". */
  slug: string;
  /** Client-facing description shown on the Door B landing (plan §6b). */
  description: string;

  /* — universal DEFAULTS (editable in Settings; per-send override allowed) — */
  /** Default call length in minutes (Discovery locked 30; adjustable). */
  defaultDurationMin: number;
  /** Default gap after a call (Discovery 15). null → settings default. */
  defaultBufferMin: number | null;
  /** Default price (Discovery free). */
  defaultPrice: TypePrice;
  /** Default host — FK → staff/{id}. Only Rohit active today; model supports more. */
  defaultHostStaffId: string;

  /** Audience tag for this type (plan §6). */
  audience: Audience;
  /** This type's windows, or "use the global five". */
  availability: TypeAvailability;

  /** Public evergreen link vs private / partner-only (plan §6b). */
  linkVisibility: 'public' | 'private';
  /** false → hidden everywhere (soft-retire without delete). */
  active: boolean;

  /* — audit (ISO strings; serverTimestamp filled in the wrapper) — */
  createdAt: string;
  updatedAt: string;
  createdBy: string; // actor name
  updatedBy: string;
}

/* ════════════════════ consultations/{id} ════════════════════ */

export type ConsultationStatus =
  | 'pending' //      created, calendar/Meet not yet confirmed (Door B pre-confirm)
  | 'confirmed' //    calendar event + Meet link created, invite sent
  | 'cancelled' //    cancelled by staff or client
  | 'rescheduled' //  superseded by a new slot (carries rescheduledToId)
  | 'no_show' //      client didn't attend
  | 'done'; //        call completed

/** Which front door produced the booking (plan §1). */
export type ConsultationSource = 'door_a_staff' | 'door_b_selfserve';

/**
 * Frozen copy of the RESOLVED type values at booking time. Later edits to
 * `appointmentTypes` defaults must NOT rewrite history (cf. projectSeed gate
 * freezing in leads.ts). Records the values AFTER override resolution.
 */
export interface ConsultationTypeSnapshot {
  typeId: string; //          FK → appointmentTypes/{id}
  slug: string;
  name: string;
  durationMin: number; //     resolved (override → type default)
  bufferMin: number; //       resolved
  price: TypePrice; //        resolved
  audience: Audience;
}

/** Client-supplied fields captured at booking (Door B form, plan §6b). */
export interface ConsultationClient {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  country: string;
  projectType: string;
  /** Optional free-text brief. */
  brief: string;
}

export interface Consultation {
  /** Stable id (CN-#### serial; Firestore autoId becomes authoritative on
   *  persist, exactly like leadStore.createLead returns ref.id). */
  id: string;

  /** Resolved, frozen type info (history-safe). */
  type: ConsultationTypeSnapshot;
  /** Resolved host — FK → staff/{id}. */
  hostStaffId: string;

  /** Lead this booking belongs to, when known. null for a cold Door B booking. */
  leadId: string | null;
  /** clients/{cid} Person Data Hub, when resolved (mirrors Lead.contactId). */
  contactId: string | null;

  /** What the client gave us at booking. */
  client: ConsultationClient;

  /* — when (stored anchored to timeZone; rendered in viewer's zone) — */
  startISO: string; //   ISO with offset, anchored to timeZone
  endISO: string; //     startISO + durationMin
  /** IANA zone the slot is anchored in (Asia/Kolkata — the bookable truth). */
  timeZone: string;

  /* — Google integration (filled by the booking Cloud Function) — */
  googleCalendarEventId: string | null;
  googleMeetUrl: string | null;

  status: ConsultationStatus;
  source: ConsultationSource;

  /** Token that produced this booking, when via a link (FK → bookingLinks/{token}). */
  bookingTokenId: string | null;
  /** When rescheduled, the new consultation that supersedes this one. */
  rescheduledToId: string | null;

  /* — audit (ISO strings) — */
  createdBy: string; //        actor name; 'client (self-serve)' for Door B
  createdByUid: string | null;
  createdAt: string;
  updatedAt: string;
  /** Append-only audit trail (mirrors leadDoc.team_activity). */
  activity: Array<{ at: string; by: string; what: string }>;
}

/* ════════════════════ Defaults → override resolution inputs ════════════════════ */

/** A per-send override (set on a bookingLink, or by staff in the Door A panel). */
export interface BookingOverride {
  durationMin?: number;
  bufferMin?: number;
  hostStaffId?: string;
  price?: TypePrice;
}

/* ════════════════════ bookingLinks/{token} ════════════════════ */

export type BookingLinkVariant = 'per_type_public' | 'lead_specific';

export interface BookingLink {
  /** Doc id = the unguessable token (CF-minted, ≥128-bit; this IS the URL key:
   *  /book/{token}). Same id-as-token idiom as intake linkId. */
  token: string;
  variant: BookingLinkVariant;

  /** Which type this link books — FK + denormalised slug for the URL. */
  typeId: string;
  typeSlug: string;

  /** Lead-specific links carry the lead so the booking auto-stamps it. */
  leadId: string | null;
  contactId: string | null;

  /** Pre-fill for the Door B form (lead-specific links, plan §6b). */
  prefill: Partial<ConsultationClient> | null;

  /** The per-send override applied to bookings made via this link. */
  override: BookingOverride | null;

  /** Mirrors the type's visibility; private = partner-only links (plan §6b). */
  visibility: 'public' | 'private';
  active: boolean;
  /** Optional expiry (lead-specific links can be short-lived). */
  expiresAt: string | null;

  /** Set once a booking is made from this link (idempotency / audit). */
  usedConsultationId: string | null;

  createdBy: string;
  createdAt: string;
}

/* ════════════════════ Id mints (pure, deterministic) ════════════════════ */

/**
 * Deterministic appointment-type id mint — caller supplies a monotone seq so
 * tests / seed are reproducible. Format AT-#### (4-digit padded), the same
 * portal-native serial discipline as `leadId`.
 */
export function appointmentTypeId(seq: number): string {
  return `AT-${String(seq).padStart(4, '0')}`;
}

/**
 * Deterministic consultation id mint. Format CN-#### (4-digit padded). On
 * persist the Firestore autoId becomes authoritative (cf. leadStore.createLead),
 * with CN-#### usable as a display label.
 */
export function consultationId(seq: number): string {
  return `CN-${String(seq).padStart(4, '0')}`;
}
